import React, { FC, useContext, useEffect, useState } from "react";
import cx from "classnames";
import Select, { Styles } from "react-select";
import { Divider } from "@pepdirect/v3/ui/Divider";
import { DropdownIndicator } from "@pepdirect/v3/ui/DropdownIndicator";
import { customReactSelectStyles } from "@pepdirect/v3/constants/dropdown";
import { ReactSelectDropdownOption } from "@pepdirect/v3/types/dropdown";
import { logEvent } from "@pepdirect/v3/helpers/analyticsLogger";
import { EVENT_NAMES } from "@pepdirect/v3/types";
import { UserContext } from "context/user";
import { Subscription_Options } from "constants/subscription";
import { convertFromCentsToDollars } from "helpers/currency";
import { Discount } from "services/graphql/generated";
import st from "./priceChooser.module.scss";

const intervalStyles: Partial<Styles<ReactSelectDropdownOption, false>> = {
  ...customReactSelectStyles,
  control: (provided) => ({
    ...provided,
    borderRadius: 4,
    minHeight: "30px",
    minWidth: "132px",
    padding: "0 8px",
  }),
  singleValue: () => ({
    textAlign: "center",
    width: "100%",
  }),
};

const { SUB_SAVE, ONE_TIME } = Subscription_Options;

interface PriceChooserProps {
  discount?: Partial<Discount> | null;
  intervals: number[];
  onIntervalChange: (interval: number) => void;
  onSubscriptionOptionChange: (optIn: Subscription_Options) => void;
  price: string;
  selectedInterval: number;
  subscriptionOption: Subscription_Options;
  subscriptionPrice: string | null;
}

interface PriceOption {
  label: string;
  value: string;
}

export const PriceChooser: FC<PriceChooserProps> = ({
  discount,
  intervals,
  onIntervalChange,
  onSubscriptionOptionChange,
  price,
  selectedInterval,
  subscriptionOption,
  subscriptionPrice,
}) => {
  const { currentUserId } = useContext(UserContext);
  const { amount, isPercent } = discount || {};
  const discountLabel =
    (amount &&
      (isPercent
        ? `& save ${amount}%`
        : `& save ${convertFromCentsToDollars(amount)}`)) ||
    "";
  const mapPriceOption = (days: number): PriceOption => ({
    label: `${days} days`,
    value: `${days}`,
  });
  const [selectedMappedInterval, setSelectedMappedInterval] =
    useState<PriceOption>(mapPriceOption(selectedInterval));

  useEffect(
    () => setSelectedMappedInterval(mapPriceOption(selectedInterval)),
    [selectedInterval]
  );

  const handleClick = (option: Subscription_Options) => () => {
    onSubscriptionOptionChange(option);
    logEvent(
      currentUserId,
      option === ONE_TIME
        ? EVENT_NAMES.productOneTimePurchaseSelected
        : EVENT_NAMES.productSubAndSaveSelected
    );
  };

  const isOneTime = subscriptionOption === ONE_TIME;
  const isSubSave = subscriptionOption === SUB_SAVE;

  const options: PriceOption[] = intervals.map(mapPriceOption);

  return (
    <div className={st.wrapper}>
      <div
        className={cx(
          st.oneTimePurchaseContainer,
          isSubSave && st.grayBackground
        )}
        onClick={handleClick(ONE_TIME)}
      >
        <input
          checked={isOneTime}
          className={st.radio}
          name="price-option"
          onChange={() => {}}
          type="radio"
        />
        <div>
          <div className={st.optionTitle}>One-time purchase</div>
          <div className={cx(st.optionPrice, isOneTime && st.selected)}>
            {price}
          </div>
        </div>
      </div>
      <Divider />
      <div
        className={cx(
          st.subscribeAndSaveContainer,
          isOneTime && st.grayBackground
        )}
        onClick={handleClick(SUB_SAVE)}
      >
        <div className={st.subscribeAndSave}>
          <input
            checked={isSubSave}
            className={st.radio}
            name="price-option"
            onChange={() => {}}
            type="radio"
          />
          <div>
            <div className={st.optionTitle}>{`Subscribe ${discountLabel}`}</div>
            <span className={cx(st.optionPrice, isSubSave && st.selected)}>
              {subscriptionPrice || price}
            </span>
            {subscriptionPrice && subscriptionPrice !== price && (
              <span className={st.originalPrice}>{price}</span>
            )}
          </div>
        </div>
        {isSubSave && (
          <div className={st.intervalContainer}>
            <div className={st.intervalTitle}>Deliver every:</div>
            <Select
              components={{ DropdownIndicator }}
              inputId="interval"
              isSearchable={false}
              onChange={(newValue) => {
                newValue && onIntervalChange(+newValue.value);
              }}
              options={options}
              styles={intervalStyles}
              value={selectedMappedInterval}
            />
          </div>
        )}
      </div>
    </div>
  );
};
